import { z } from 'zod';

import { DateLikeSchema, UncefactUnitSchema } from '@vestigas/utils';
import { i18n } from '~/utils';

import { DeliveryPartyModel } from './zodDeliveryPartyModel';

const ConcreteComponentModel = z.object({
  comment: z.string().optional(),
  description: z.string().optional(),
  manufacturer: DeliveryPartyModel.optional(),
  name: z.string().optional(),
  quantity: z
    .object({
      value: z.number().or(z.string()).nullish(),
      measure: z.string().optional(),
    })
    .superRefine((data, context) => {
      const hasValue =
        (typeof data.value === 'string' && data.value.trim() !== '') ||
        typeof data.value === 'number';
      const hasUnit =
        typeof data.measure === 'string' && data.measure.trim() !== '';

      // If a value is set, we require a unit to be set as well
      if (hasValue && !hasUnit) {
        context.addIssue({
          code: 'invalid_type', // show common "must not be empty" error
          expected: 'string',
          path: ['measure'],
          received: 'undefined',
        });
      }
    }),
  requested: z
    .object({
      value: z.number().or(z.string()).optional(),
      measure: z.string().optional(),
    })
    .superRefine((data, context) => {
      const hasValue =
        (typeof data.value === 'string' && data.value.trim() !== '') ||
        typeof data.value === 'number';
      const hasUnit =
        typeof data.measure === 'string' && data.measure.trim() !== '';

      // If a value is set, we require a unit to be set as well
      if (hasValue && !hasUnit) {
        context.addIssue({
          code: 'invalid_type', // show common "must not be empty" error
          expected: 'string',
          path: ['measure'],
          received: 'undefined',
        });
      }
    }),
  type: z.string().optional(),
});

const WeighingWeightSchema = z
  .object({
    date: DateLikeSchema.optional(),
    id: z.string().optional(),
    measure: z
      .union([
        z.literal(''),
        z
          .string()
          .regex(/^[a-z\d]{2,3}$/i, {
            message: i18n.t(($) => $.invoiceForm.field.items.unitType.error, {
              ns: 'invoiceReprocessing',
            }),
          })
          .toUpperCase(),
      ])
      .optional(),
    weight: z.number().or(z.string()).nullish(),
  })
  .superRefine((data, context) => {
    const hasValue =
      (typeof data.weight === 'string' && data.weight.trim() !== '') ||
      typeof data.value === 'number';
    const hasUnit =
      typeof data.measure === 'string' && data.measure.trim() !== '';

    // If a value is set, we require a unit to be set as well
    if (hasValue && !hasUnit) {
      context.addIssue({
        code: 'invalid_type', // show common "must not be empty" error
        expected: 'string',
        path: ['measure'],
        received: 'undefined',
      });
    }
  });

/**
 * Zod validation schema for delivery note line item.
 */
export const LineItemModel = z.object({
  agreement: z.object({
    buyerOrder: z.string().optional(),
    sellerOrder: z.string().optional(),
  }),
  delivery: z
    .object({
      actualDelivery: z
        .object({
          measure: UncefactUnitSchema.nullish(),
          value: z.number().or(z.string()).optional(),
        })
        .superRefine((data, context) => {
          const hasValue =
            (typeof data.value === 'string' && data.value.trim() !== '') ||
            typeof data.value === 'number';
          const hasUnit =
            typeof data.measure === 'string' && data.measure.trim() !== '';

          // If a value is set, we require a unit to be set as well
          if (hasValue && !hasUnit) {
            context.addIssue({
              code: 'invalid_type', // show common "must not be empty" error
              expected: 'string',
              path: ['measure'],
              received: 'undefined',
            });
          }
        }),
      netWeight: z
        .object({
          measure: z.union([z.literal(''), UncefactUnitSchema]).optional(),
          weight: z.number().or(z.string()).nullish(),
        })
        .superRefine((data, context) => {
          const hasValue =
            (typeof data.weight === 'string' && data.weight.trim() !== '') ||
            typeof data.value === 'number';
          const hasUnit =
            typeof data.measure === 'string' && data.measure.trim() !== '';

          // If a value is set, we require a unit to be set as well
          if (hasValue && !hasUnit) {
            context.addIssue({
              code: 'invalid_type', // show common "must not be empty" error
              expected: 'string',
              path: ['measure'],
              received: 'undefined',
            });
          }
        }),
      productUnit: z
        .object({
          quantity: z.number().or(z.string().min(1)),
          quantityType: UncefactUnitSchema,
        })
        .superRefine((data, context) => {
          const hasValue =
            (typeof data.quantity === 'string' &&
              data.quantity.trim() !== '') ||
            typeof data.value === 'number';
          const hasUnit =
            typeof data.quantityType === 'string' &&
            data.quantityType.trim() !== '';

          // If a value is set, we require a unit to be set as well
          if (hasValue && !hasUnit) {
            context.addIssue({
              code: 'invalid_type', // show common "must not be empty" error
              expected: 'string',
              path: ['quantityType'],
              received: 'undefined',
            });
          }
        }),
      remainingDelivery: z
        .object({
          measure: UncefactUnitSchema.nullish(),
          value: z.number().or(z.string()).nullish(),
        })
        .superRefine((data, context) => {
          const hasValue =
            (typeof data.value === 'string' && data.value.trim() !== '') ||
            typeof data.value === 'number';
          const hasUnit =
            typeof data.measure === 'string' && data.measure.trim() !== '';

          // If a value is set, we require a unit to be set as well
          if (hasValue && !hasUnit) {
            context.addIssue({
              code: 'invalid_type', // show common "must not be empty" error
              expected: 'string',
              path: ['measure'],
              received: 'undefined',
            });
          }
        }),
      requestedDelivery: z
        .object({
          measure: UncefactUnitSchema.nullish(),
          value: z.number().or(z.string()).nullish(),
        })
        .superRefine((data, context) => {
          const hasValue =
            (typeof data.value === 'string' && data.value.trim() !== '') ||
            typeof data.value === 'number';
          const hasUnit =
            typeof data.measure === 'string' && data.measure.trim() !== '';

          // If a value is set, we require a unit to be set as well
          if (hasValue && !hasUnit) {
            context.addIssue({
              code: 'invalid_type', // show common "must not be empty" error
              expected: 'string',
              path: ['measure'],
              received: 'undefined',
            });
          }
        }),
    })
    .superRefine((data, context) => {
      // Only validate when both units are the same
      if (
        !data.productUnit.quantityType ||
        data.productUnit.quantityType !== data.netWeight.measure
      ) {
        return;
      }

      // This is way too much effort :-(
      // - try casting to Number for comparison
      // - if that fails, try converting German number format to international format and try again
      const productQuantityNum = Number.isNaN(
        Number(data.productUnit.quantity ?? ''),
      )
        ? Number(
            String(data.productUnit.quantity ?? '')
              .trim()
              .replace('.', '')
              .replace(',', '.'),
          )
        : Number(data.productUnit.quantity ?? '');
      const netWeightNum = Number.isNaN(Number(data.netWeight.weight))
        ? Number(
            String(data.netWeight.weight ?? '')
              .trim()
              .replace('.', '')
              .replace(',', '.'),
          )
        : Number(data.netWeight.weight ?? '');

      if (
        Number.isNaN(productQuantityNum) ||
        Number.isNaN(netWeightNum) ||
        productQuantityNum !== netWeightNum
      ) {
        // Show errors on productUnit.quantity and netWeight.weight fields
        context.addIssue({
          code: 'custom',
          message: i18n.t(
            ($) => $.deliveryForm.lineItemQuantityAndWeightMustMatchError,
            {
              ns: 'deliveryNotes',
            },
          ),
          path: ['productUnit', 'quantity'],
        });
        context.addIssue({
          code: 'custom',
          message: i18n.t(
            ($) => $.deliveryForm.lineItemQuantityAndWeightMustMatchError,
            {
              ns: 'deliveryNotes',
            },
          ),
          path: ['netWeight', 'weight'],
        });
      }
    }),
  documentLine: z.object({
    note: z.object({
      content: z.object({
        grossWeight: WeighingWeightSchema,
        tareWeight: WeighingWeightSchema,
        weighingParty: z.object({}),
        weighingPerson: z.object({}),
      }),
    }),
  }),
  id: z.string().min(1),
  product: z.object({
    concrete: z
      .object({
        chlorideClass: z.string().optional(),
        chlorideContent: z
          .object({
            value: z.number().or(z.string()).nullish(),
            measure: z.string().optional(),
          })
          .superRefine((data, context) => {
            const hasValue =
              (typeof data.value === 'string' && data.value.trim() !== '') ||
              typeof data.value === 'number';
            const hasUnit =
              typeof data.measure === 'string' && data.measure.trim() !== '';

            // If a value is set, we require a unit to be set as well
            if (hasValue && !hasUnit) {
              context.addIssue({
                code: 'invalid_type', // show common "must not be empty" error
                expected: 'string',
                path: ['measure'],
                received: 'undefined',
              });
            }
          }),
        comment: z.string().optional(),
        components: z.array(ConcreteComponentModel),
        consistency: z.string().optional(),
        exposureClass: z.string().optional(),
        grossDensityClass: z.string().optional(),
        largestGrain: z.string().optional(),
        lastValidation: DateLikeSchema.optional(),
        moistureClass: z.string().optional(),
        processabilityTime: z.number().or(z.string()).optional(),
        returnedMaterial: z
          .object({
            value: z.number().or(z.string()).nullish(),
            measure: z.string().optional(),
          })
          .superRefine((data, context) => {
            const hasValue =
              (typeof data.value === 'string' && data.value.trim() !== '') ||
              typeof data.value === 'number';
            const hasUnit =
              typeof data.measure === 'string' && data.measure.trim() !== '';

            // If a value is set, we require a unit to be set as well
            if (hasValue && !hasUnit) {
              context.addIssue({
                code: 'invalid_type', // show common "must not be empty" error
                expected: 'string',
                path: ['measure'],
                received: 'undefined',
              });
            }
          }),
        strengthClass: z.string().optional(),
        strengthDevelopment: z.string().optional(),
        type: z.string().optional(),
        usage: z.string().optional(),
        waterCementRatio: z.number().or(z.string()).optional(),
      })
      .optional(),
    description: z.string().optional(),
    ean: z.string().optional(),
    name: z.string().min(1),
    sellerAssignedId: z.string().optional(),
  }),
  settlement: z.object({
    payableAccount: z.object({ name: z.string().optional() }),
  }),
});
